import { Injectable, inject } from '@angular/core';

import { EMPTY, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';

import { AlarmService, ClimateService, IReckonTelevisionService, LightsService } from '@era/inroom-sdk';

import { HotelRoomApiModel } from '@era/inroom-sdk';
import { MediaStateChangedEvent } from '../shared/events/media-state-changed.event';
import { RoomActionTypes } from '../stores/rooms/rooms.actions';
import { LoadRoomConfiguration } from '../sub-features/room-info/room-configuration-store/room-configuration.actions';
import {
  AlarmApiAlarmLoaded,
  AlarmApiNoAlarm,
  AlarmLocationModel,
} from '../sub-features/room/alarm/alarm-store/alarm.actions';
import {
  FanspeedLocationModel,
  LoadFanspeed,
  NoFanspeed,
} from '../sub-features/room/fanspeed/fanspeed-store/fanspeed.actions';
import { LightLocationModel, LoadLights, NoLight } from '../sub-features/room/lights/lights-store/lights.actions';
import { MediaSignalRStateChanged } from '../sub-features/room/media/media.actions';
import {
  LoadTemperature,
  NoTemperature,
  TemperatureLocationModel,
} from '../sub-features/room/temperature/temperature-store/temperature.actions';

@Injectable()
export class SelectionEffects {
  private readonly actions$ = inject(Actions);
  private readonly alarmApi = inject(AlarmService);
  private readonly climateApi = inject(ClimateService);
  private readonly televisionService = inject(IReckonTelevisionService);
  private readonly lightsApi = inject(LightsService);

  loadAlarmOnSelected$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_RESOLVER_ROOM_SELECTED),
      switchMap((state: { payload: HotelRoomApiModel }) => {
        if (!state.payload.HotelCode || !state.payload.Room) {
          return of(new AlarmApiNoAlarm());
        }
        return this.alarmApi.getAlarm({ hotelCode: state.payload.HotelCode, room: state.payload.Room }).pipe(
          map((alarm) => {
            if (!alarm.Data || !state.payload.HotelCode) {
              return new AlarmApiNoAlarm();
            }
            const tempAlarm = {
              room: alarm.Data.Room,
              state: alarm.Data.State,
              time: alarm.Data.Time,
              typeCode: alarm.Data.TypeCode,
            } as AlarmLocationModel;
            tempAlarm.hotelCode = state.payload.HotelCode;
            return new AlarmApiAlarmLoaded(tempAlarm);
          })
        );
      })
    );
  });

  loadFanspeedOnSelected$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_RESOLVER_ROOM_SELECTED),
      switchMap((state: { payload: HotelRoomApiModel }) => {
        const { HotelCode, Room } = state.payload;
        if (!HotelCode || !Room) {
          return of(new NoFanspeed());
        }
        return this.climateApi

          .getfanspeed({ hotelCode: HotelCode, room: Room, baseApiModel: {} })

          .pipe(
            map((temperature) => {
              const tempFanspeed = temperature.Data?.[0] as FanspeedLocationModel;
              if (!tempFanspeed) {
                return new NoFanspeed();
              }
              tempFanspeed.HotelCode = HotelCode;
              tempFanspeed.Room = Room;
              return new LoadFanspeed(tempFanspeed);
            })
          );
      })
    );
  });

  loadTemperatureOnSelected$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_RESOLVER_ROOM_SELECTED),
      switchMap((state: { payload: HotelRoomApiModel }) => {
        const { HotelCode, Room } = state.payload;
        if (!HotelCode || !Room) {
          return of(new NoTemperature());
        }
        return this.climateApi

          .gettemperature({
            hotelCode: HotelCode,
            room: Room,
            baseApiModel: {},
          })

          .pipe(
            map((temperature) => {
              const tempTemperature = temperature.Data?.[0] as TemperatureLocationModel;
              if (!tempTemperature) {
                return new NoTemperature();
              }
              tempTemperature.HotelCode = HotelCode;
              tempTemperature.Room = Room;
              return new LoadTemperature(tempTemperature);
            })
          );
      })
    );
  });

  loadMediaStateOnSelected$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_RESOLVER_ROOM_SELECTED),
      filter(
        (state: { payload: HotelRoomApiModel } | undefined): state is { payload: HotelRoomApiModel } =>
          state !== undefined
      ),
      switchMap((state: { payload: HotelRoomApiModel }) => {
        const { HotelCode, Room } = state.payload;
        if (!HotelCode || !Room) {
          return EMPTY;
        }
        return this.televisionService
          .getMediaState({ hotelCode: HotelCode, room: Room })
          .pipe(map((result) => ({ data: result, hotelCode: HotelCode, room: Room })));
      }),
      filter((result) => result.data?.Data?.length !== 0),
      map((mediaStateResult) => {
        const tempMediaState = mediaStateResult.data?.Data?.[0] as MediaStateChangedEvent;
        tempMediaState.HotelCode = mediaStateResult.hotelCode;
        tempMediaState.UnitCode = mediaStateResult.room;
        return new MediaSignalRStateChanged({ mediaState: tempMediaState });
      })
    );
  });

  loadLightsOnSelected$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_RESOLVER_ROOM_SELECTED),
      switchMap((state: { payload: HotelRoomApiModel }) => {
        const { HotelCode, Room } = state.payload;
        if (!HotelCode || !Room) {
          return of(new NoLight());
        }
        return this.lightsApi.all({ hotelCode: HotelCode, room: Room }).pipe(
          map((lights) => {
            const tempLight = lights.Data as LightLocationModel[];
            if (tempLight === null) {
              return new NoLight();
            }

            tempLight.forEach((light) => {
              light.HotelCode = HotelCode;
              light.Room = Room;
            });

            return new LoadLights(tempLight);
          })
        );
      })
    );
  });

  loadRoomConfigurationOnSelected$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoomActionTypes.ROOM_RESOLVER_ROOM_SELECTED),
      map((state: { payload: HotelRoomApiModel }) => {
        const { HotelCode, Room } = state.payload;
        return new LoadRoomConfiguration({ hotelCode: HotelCode ?? '', unitCode: Room ?? '' });
      })
    );
  });
}
